/**
 * Clause library and clause matching — FR-16, FR-17, FR-25…FR-28, §4.
 */
import { z } from 'zod';
import { ClauseCodeSchema, ContractKeySchema, IsoTimestampUtcSchema, OpaqueIdSchema, TextSpanSchema, UnitScoreSchema, UpnSchema, VersionNoSchema, } from '../primitives.js';
import { ClauseMatchStateSchema, ClauseRepresentationSchema, ConfidenceBandSchema, SearchJudgementSchema, } from '../vocabulary.js';
import { provenanced } from '../provenance.js';
/* -------------------------------------------------------------------------- */
/* Library clause — FR-16 / FR-17                                              */
/* -------------------------------------------------------------------------- */
/**
 * A clause in the standard library.
 *
 * `clause_code` is the addressable identity throughout this contract; `clause_id`
 * is exposed as an opaque string only because reason traces and review queues
 * need to point at a specific row. Never depend on it being numeric.
 */
export const LibraryClauseSchema = z.object({
    clause_id: OpaqueIdSchema,
    clause_code: ClauseCodeSchema,
    title: z.string().min(1).max(200),
    category: z.string().max(60).nullable(),
    /** FR-17 — only a reviewer may edit this, and an edit creates a new version. */
    master_text: z.string().min(1),
    version_no: VersionNoSchema,
    is_active: z.boolean(),
    /**
     * WP-09 §4 representation 3 — a plain-language description of what the clause
     * does, generated once and regenerated only on revision. It exists because
     * *"cap our liability"* is not semantically adjacent to *"aggregate liability
     * shall not exceed"*; this is the text that closes that gap.
     */
    purpose_text: provenanced(z.string().max(1000).nullable()),
    /**
     * WP-09 — manual authority. Positive pins a clause up, negative buries it.
     * `λ_b = 0.10` in the ranking formula; the effect is bounded by design.
     */
    boost_weight: z.number().int(),
    /**
     * Why `boost_weight` was last set — `set_clause_boost.reason`, landed as a
     * column by migration 0085 (D-B10, CLM-39) so it reaches the trigger's audit
     * row (NFR-13). `null` until a boost is set. Required-nullable, not optional:
     * a silent absence is the evidence gap the column exists to close.
     */
    boost_reason: z.string().max(500).nullable(),
    last_edited_by_upn: UpnSchema.nullable(),
    last_edited_utc: IsoTimestampUtcSchema.nullable(),
});
/* -------------------------------------------------------------------------- */
/* Clause instance on a contract                                               */
/* -------------------------------------------------------------------------- */
/**
 * One paragraph of one contract, and what the matcher decided about it (FR-28).
 *
 * Carries a **span, not text** — CLAUDE.md rule 13. The converted document text
 * is stored once; re-serving slices of it through every clause row would
 * duplicate it, and the front end can render from the span against the text it
 * already fetched.
 */
export const ContractClauseSchema = z.object({
    contract_clause_id: OpaqueIdSchema,
    contract_key: ContractKeySchema,
    /** Paragraph reference as numbered by the document itself, e.g. `12.3`. */
    paragraph_ref: z.string().max(60).nullable(),
    span: TextSpanSchema,
    /**
     * The library clause this paragraph was matched to. `null` means **bespoke** —
     * no library match, and a candidate new library clause (§4.2 `LOW`).
     */
    clause_code: ClauseCodeSchema.nullable(),
    /** Version matched against. A later library revision makes this stale, not wrong. */
    clause_version_no: VersionNoSchema.nullable(),
    /**
     * `Standard`, `Modified` or `Rejected`. `null` while unadjudicated.
     *
     * `Modified` is the judgement that matters: a high cosine score says *same
     * topic*, not *same terms*. Recording an altered liability cap as `Standard` is
     * the one error class the four-stage matcher exists to prevent (§4.1).
     */
    match_state: ClauseMatchStateSchema.nullable(),
    confidence_band: ConfidenceBandSchema.nullable(),
    deviation_note: z.string().max(2000).nullable(),
    /** Shown in the review surface. Produced by the adjudication step, not the UI. */
    match_reasoning: z.string().max(4000).nullable(),
    /**
     * True when stage ② settled it — normalised similarity ≥ 0.95, **no LLM call**.
     * Most reuse is pasted verbatim, so this is provable rather than inferred, and
     * it is the real cost saving in §4.1.
     */
    settled_by_near_exact_match: z.boolean(),
    /** Set once a reviewer has confirmed or corrected the match (feedback loop 2). */
    reviewed_by_upn: UpnSchema.nullable(),
    reviewed_utc: IsoTimestampUtcSchema.nullable(),
});
/* -------------------------------------------------------------------------- */
/* Search behaviour — WP-09 §6, NFR-13                                         */
/* -------------------------------------------------------------------------- */
/**
 * A curated query-vocabulary entry — WP-09 layer 1. Deterministic and instant:
 * `LoL` → `limitation of liability`.
 */
export const ClauseSearchAliasSchema = z.object({
    alias_id: OpaqueIdSchema,
    alias_term: z.string().min(1).max(120),
    canonical_term: z.string().min(1).max(200),
    /**
     * `Mined` aliases are *proposed* by the system from repeated corrections and
     * approved by a human — WP-09 §6 loop 2. The system proposes; a person approves.
     * Same governance as party aliases and role mappings.
     */
    origin: z.enum(['Manual', 'Mined']),
    is_active: z.boolean(),
    created_by_upn: UpnSchema.nullable(),
    created_utc: IsoTimestampUtcSchema,
});
/**
 * A recorded relevance judgement — the learning signal (FR-26, WP-09 §6).
 *
 * Corrections are **data, not code**: every learned behaviour is a row an admin
 * can inspect, edit and delete, and deleting it removes the effect on the next
 * query with no retraining and no cache to invalidate.
 */
export const ClauseSearchJudgementSchema = z.object({
    feedback_id: OpaqueIdSchema,
    query_text: z.string().min(1).max(400),
    clause_code: ClauseCodeSchema,
    /**
     * Staleness anchor. A judgement recorded against version 2 is weak evidence
     * about version 4 — its weight drops to 0.3 and it queues for re-confirmation
     * (WP-09 §6, §4.3 loop 3).
     */
    clause_version_no: VersionNoSchema,
    judgement: SearchJudgementSchema,
    judged_by_upn: UpnSchema,
    judged_utc: IsoTimestampUtcSchema,
    is_active: z.boolean(),
    /** True when the clause has been revised since — the 0.3 weight applies. */
    is_stale: z.boolean(),
    /** `1.0` fresh, `0.3` stale — `vw_clause_search_judgement.staleness_weight` (0026). */
    staleness_weight: z.number().min(0).max(1),
    /** The clause's current version, or `null` when every version is retired (view 0026). */
    current_version_no: VersionNoSchema.nullable(),
    /** The logged search the judgement was recorded from; `null` for a free-text judgement. */
    search_id: z.string().nullable(),
    /** Which embedding space `query_embedding` lives in — 0085 (D-B10, CLM-39); mirrors 0022's bound. */
    embedding_model: z.string().min(1).max(60),
});
/** Provenance of one clause embedding — WP-09 §8, recorded per representation. */
export const ClauseEmbeddingInfoSchema = z.object({
    representation: ClauseRepresentationSchema,
    embedding_model: z.string().min(1).max(60),
    dimensions: z.number().int().min(1),
    generated_utc: IsoTimestampUtcSchema,
    /** Best cosine achieved by this representation for the query at hand. */
    cosine: UnitScoreSchema.nullable(),
});
//# sourceMappingURL=clause.js.map